import type React from "react";
import { useState } from "react";
import {
  Box,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { IconSearch } from "@tabler/icons-react";
import { searchApi } from "@services/searchApi";
import type { SearchResponse } from "@models/search";
import MatchTierBadge from "@components/MatchTierBadge";
import ResultCard from "@components/ResultCard";
import SearchConfig, { type IndexOption } from "@components/SearchConfig";
import SearchHero from "@components/SearchHero";

// `rmi-topictree-wd-oem-de` was `rmi-topictree-psa-de` until the wiring-diagram
// service was renamed. The index is renamed per environment, so this option
// resolves nothing on an environment the rename has not reached yet.
const INDEX_OPTIONS: IndexOption[] = [
  { value: "rmi-topictree-tm-en", label: "Technical Manual" },
  { value: "rmi-topictree-wd-oem-de", label: "WD (OE)" },
];

const MIN_QUERY_LENGTH = 3;

const SearchPage: React.FC = () => {
  const [indexId, setIndexId] = useState(INDEX_OPTIONS[0].value);
  const [topK, setTopK] = useState(10);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SearchResponse | null>(null);

  const runSearch = async (): Promise<void> => {
    const text = query.trim();
    if (text.length < MIN_QUERY_LENGTH) return;
    setLoading(true);
    setError(null);
    try {
      const res = await searchApi.search({
        indexId,
        query: { type: "text", text },
        topK,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const hits = result?.hits ?? [];
  const hasResults = result !== null;

  const selectedIndexLabel =
    INDEX_OPTIONS.find((option) => option.value === indexId)?.label ?? "index";

  return (
    <Container size="xl" pb="4rem">
      <Stack gap="lg" align="center" justify="flex-start">
        <SearchHero />

        <Paper
          withBorder
          radius="md"
          bg="white"
          p={6}
          w="100%"
          maw={1100}
        >
          <Group gap="xs" wrap="nowrap">
            <SearchConfig
              indexOptions={INDEX_OPTIONS}
              indexId={indexId}
              onIndexChange={setIndexId}
              topK={topK}
              onTopKChange={setTopK}
            />
            <TextInput
              flex={1}
              size="md"
              variant="unstyled"
              placeholder={`Search the ${selectedIndexLabel}…`}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void runSearch();
              }}
            />
            <Button
              size="md"
              radius="sm"
              color="brand"
              leftSection={<IconSearch size={18} />}
              onClick={() => void runSearch()}
              loading={loading}
            >
              Search
            </Button>
          </Group>
        </Paper>

        <Box w="100%" maw={1100}>
          {loading && (
            <Center mih={160}>
              <Loader />
            </Center>
          )}

          {error && !loading && (
            <Alert variant="light" color="red" title="Search failed">
              {error}
            </Alert>
          )}

          {hasResults && !loading && hits.length === 0 && (
            <Stack gap="sm" align="center" mih={160} justify="center">
              <Text c="dimmed">No results.</Text>
              <MatchTierBadge
                tier={result?.matchTier}
                degraded={result?.degraded}
              />
            </Stack>
          )}

          {hasResults && !loading && hits.length > 0 && (
            <Stack gap="md">
              <Group justify="space-between" wrap="nowrap" align="center">
                <Text size="sm" c="dimmed">
                  {hits.length} result{hits.length === 1 ? "" : "s"}
                </Text>
                <MatchTierBadge
                  tier={result?.matchTier}
                  degraded={result?.degraded}
                />
              </Group>
              {hits.map((hit) => (
                <ResultCard key={hit.id} hit={hit} />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Container>
  );
};

export default SearchPage;
